
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { isAuthenticated, isSuperAdmin, logoutUser, getCurrentUser } from '@/utils/auth';
import { safeUserRole } from '@/utils/roleUtils';
import { SaasInstance } from '@/types/userRole';

export const useSuperAdminInstances = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [instances, setInstances] = useState<SaasInstance[]>([]);

  useEffect(() => {
    const checkAuth = async () => {
      const currentUser = getCurrentUser();
      const userRole = currentUser?.role || '';
      
      if (!isAuthenticated() || !isSuperAdmin(safeUserRole(userRole))) {
        navigate('/login');
        return;
      }
      
      setUser(currentUser);
      
      // In a real application, this would be an API call to fetch instances
      // tied to the current super admin user
      // For now, we're setting an empty array since we're removing mock data
      setInstances([]);
      setIsLoading(false);
    };
    
    checkAuth();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const handleSuspendInstance = (instanceId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'suspended' ? 'active' : 'suspended';
    const action = currentStatus === 'suspended' ? 'reactivated' : 'suspended';
    
    // In a real app, this would be an API call
    // await fetch(`/api/instances/${instanceId}/status`, { 
    //   method: 'POST', 
    //   headers: { 'Content-Type': 'application/json' },
    //   body: JSON.stringify({ status: newStatus })
    // });
    
    setInstances(instances.map(instance => 
      instance.id === instanceId 
        ? { ...instance, status: newStatus } 
        : instance
    ));
    
    toast({
      title: `Instance ${action}`,
      description: `The instance has been ${action} successfully.`,
    });
  };

  const filteredInstances = searchQuery
    ? instances.filter(instance => 
        instance.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        instance.domain.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : instances;

  return {
    isLoading,
    user,
    searchQuery,
    setSearchQuery,
    filteredInstances,
    handleLogout,
    handleSuspendInstance,
  };
};
